import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Download, ExternalLink, FileText, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { catalogueReducer } from '../../store/catalogue/catalogue.reducer';
import {
  getCatalogueIsLoadingProductDetail,
  getSelectedProduct,
  getSelectedVariant,
} from '../../store/catalogue/catalogue.selectors';
import { resolveImageSrc } from '../../utils/imageUtils';
import AddVariantModal from './AddVariantModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import IesPreview3DModal from './IesPreview3DModal';

const { selectVariant, createVariant, modifyVariant, removeVariant } = catalogueReducer.actions;

interface ProductDetailProps {
  canEdit?: boolean;
}

const ProductDetail: React.FC<ProductDetailProps> = ({ canEdit = true }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedProduct = useAppSelector(getSelectedProduct);
  const selectedVariant = useAppSelector(getSelectedVariant);
  const isLoadingProductDetail = useAppSelector(getCatalogueIsLoadingProductDetail);

  const [mainImageIndex, setMainImageIndex] = useState(0);
  const [isAddVariantModalOpen, setIsAddVariantModalOpen] = useState(false);
  const [editingVariantId, setEditingVariantId] = useState<string | null>(null);
  const [deletingVariantData, setDeletingVariantData] = useState<{ id: string; name: string } | null>(null);
  const [mainImageError, setMainImageError] = useState(false);
  const [thumbnailErrors, setThumbnailErrors] = useState<Set<number>>(new Set());
  const [isPreview3DOpen, setIsPreview3DOpen] = useState(false);

  if (!selectedProduct) {
    return null;
  }

  const primaryImage = selectedProduct.imageUrl;
  const allImages = [primaryImage, ...selectedProduct.thumbnails].filter(Boolean);
  const resolvedImages = allImages.map((img) => resolveImageSrc(img)).filter(Boolean) as string[];
  const currentImage = resolvedImages[mainImageIndex] || resolveImageSrc(primaryImage);

  const specifications = selectedVariant?.specifications || selectedProduct.variants[0]?.specifications;

  const specificationRows = specifications
    ? [
        { label: t('catalogue.specs.absolutePhotometry'), value: specifications.absolutePhotometry, mandatory: false },
        { label: t('catalogue.specs.luminousFlux'), value: specifications.luminousFlux, mandatory: true },
        { label: t('catalogue.specs.power'), value: specifications.power, mandatory: true },
        { label: t('catalogue.specs.colorTemperature'), value: specifications.colorTemperature, mandatory: true },
        { label: t('catalogue.specs.cri'), value: specifications.cri, mandatory: false },
        { label: t('catalogue.specs.lifespan'), value: specifications.lifespan, mandatory: false },
        { label: t('catalogue.specs.ipRating'), value: specifications.ipRating, mandatory: false },
      ].filter((row) => row.mandatory || row.value)
    : [];

  const handleAddOrEditVariant = (
    data: { name: string; power: number; lumenOutput: number; colorTemperature: number; manufacturerUrl?: string },
    ldcFile?: File,
  ) => {
    if (editingVariantId) {
      dispatch(modifyVariant({ id: editingVariantId, data, ldcFile }));
    } else {
      dispatch(createVariant({ productId: selectedProduct.id, data, ldcFile }));
    }
  };

  const editingVariant = editingVariantId ? selectedProduct.variants.find((v) => v.id === editingVariantId) : null;

  const handleDeleteVariant = (e: React.MouseEvent, variant: { id: string; name: string }) => {
    e.stopPropagation();
    setDeletingVariantData(variant);
  };

  const handleConfirmDelete = () => {
    if (deletingVariantData) {
      dispatch(removeVariant(deletingVariantData.id));
    }
  };

  return (
    <div className="flex-1 flex bg-black overflow-y-auto md:overflow-hidden p-4 md:p-8">
      {/* Card container with border, radius, and inner grid */}
      <div className="flex-1 border border-zinc-800 rounded-lg overflow-hidden flex flex-col md:grid md:grid-cols-2">
        {/* Left Column - Media */}
        <div className="flex flex-col p-4 md:p-6 border-b md:border-b-0 md:border-r border-zinc-800">
          {/* Main Image */}
          <div className="flex-1 flex items-center justify-center rounded-lg overflow-hidden bg-black min-h-[300px] max-h-[400px] md:max-h-none">
            {currentImage && !mainImageError ? (
              <img
                src={currentImage}
                alt={selectedProduct.name}
                className="max-w-full max-h-full object-contain"
                onError={() => setMainImageError(true)}
              />
            ) : (
              <div className="text-gray-500 text-center">
                <p className="text-sm">{t('catalogue.noImage')}</p>
              </div>
            )}
          </div>

          {/* Thumbnail Carousel */}
          {resolvedImages.length > 1 && (
            <div className="flex gap-2 overflow-x-auto py-3 mt-3">
              {resolvedImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setMainImageIndex(index);
                    setMainImageError(false);
                  }}
                  className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-colors
                    ${mainImageIndex === index ? 'border-white' : 'border-gray-700 hover:border-gray-500'}`}
                >
                  {thumbnailErrors.has(index) ? (
                    <div className="w-full h-full bg-gray-800 flex items-center justify-center text-gray-500 text-xs">
                      N/A
                    </div>
                  ) : (
                    <img
                      src={img}
                      alt={`${selectedProduct.name} ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={() => setThumbnailErrors((prev) => new Set(prev).add(index))}
                    />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Column - Information */}
        <div className="flex flex-col p-4 md:p-6 overflow-hidden">
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {/* Header */}
            <div className="mb-6 md:mb-8">
              <h1 className="text-xl md:text-2xl font-bold text-white">{selectedProduct.name}</h1>
              {(selectedProduct.subtitle || selectedProduct.description) && (
                <p className="text-gray-400 text-xs md:text-sm mt-1">
                  {selectedProduct.subtitle || selectedProduct.description}
                </p>
              )}
            </div>

            {/* Variants Section */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold text-white">{t('catalogue.variants')}</h2>
                {canEdit && (
                  <div className="flex items-center gap-3">
                    {selectedVariant && (
                      <button
                        onClick={() => setEditingVariantId(selectedVariant.id)}
                        className="flex items-center gap-1 text-green01 hover:text-green03 text-sm transition-colors"
                        title={t('catalogue.editVariantTitle')}
                      >
                        <Pencil className="w-4 h-4" />
                        {t('common.edit')}
                      </button>
                    )}
                    <button
                      onClick={() => setIsAddVariantModalOpen(true)}
                      className="flex items-center gap-1 text-green01 hover:text-green03 text-sm transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      {t('catalogue.addVariant')}
                    </button>
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {isLoadingProductDetail ? (
                  <div className="flex items-center gap-2 text-gray-400 text-sm py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.loading')}...
                  </div>
                ) : (
                  <>
                    {selectedProduct.variants.map((variant) => (
                      <div key={variant.id} className="relative group">
                        <button
                          onClick={() => dispatch(selectVariant(variant.id))}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors
                          ${
                            selectedVariant?.id === variant.id
                              ? 'bg-white text-black'
                              : 'bg-gray-800 text-gray-200 hover:bg-gray-700'
                          }`}
                        >
                          {variant.name}
                        </button>
                        {canEdit && (
                          <button
                            onClick={(e) => handleDeleteVariant(e, variant)}
                            className="absolute -top-1 -right-1 p-1 bg-red-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t('common.delete')}
                          >
                            <Trash2 className="w-3 h-3 text-white" />
                          </button>
                        )}
                      </div>
                    ))}
                    {selectedProduct.variants.length === 0 && (
                      <p className="text-gray-500 text-sm">{t('catalogue.noVariants')}</p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Specification Section */}
            {specificationRows.length > 0 && (
              <div className="mb-6 md:mb-8">
                <h2 className="text-base font-semibold text-white mb-3">{t('catalogue.specifications')}</h2>
                <div className="border-t border-zinc-800">
                  {specificationRows.map((row) => (
                    <div
                      key={row.label}
                      className="flex justify-between items-center py-2 md:py-3 px-2 md:px-4 border-b border-zinc-800 last:border-b-0"
                    >
                      <span className="text-gray-400 text-xs md:text-sm">{row.label}</span>
                      <span className="text-white text-xs md:text-sm font-medium">{row.value || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* End scrollable content area */}

          {/* Footer Actions - fixed at bottom */}
          {(selectedProduct.webUrl ||
            selectedProduct.pdfUrl ||
            selectedVariant?.ldcFileUrl ||
            selectedVariant?.manufacturerUrl) && (
            <div className="flex-shrink-0 pt-6 flex flex-wrap gap-2 md:gap-3">
              {selectedProduct.webUrl && (
                <a
                  href={selectedProduct.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-md bg-gray-800 text-white text-xs md:text-sm
                    hover:bg-gray-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('catalogue.viewOnWeb')}
                </a>
              )}
              {selectedProduct.pdfUrl && (
                <a
                  href={selectedProduct.pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-md bg-gray-800 text-white text-xs md:text-sm
                    hover:bg-gray-700 transition-colors"
                >
                  <FileText className="w-4 h-4" />
                  {t('catalogue.pdfSpec')}
                </a>
              )}
              {selectedVariant?.ldcFileUrl && (
                <a
                  href={selectedVariant.ldcFileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-md bg-gray-800 text-white text-xs md:text-sm
                    hover:bg-gray-700 transition-colors"
                  download
                >
                  <Download className="w-4 h-4" />
                  {t('catalogue.downloadLdc')}
                </a>
              )}
              {selectedVariant?.ldcFileUrl && (
                <button
                  type="button"
                  onClick={() => setIsPreview3DOpen(true)}
                  className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-md bg-gray-800 text-white text-xs md:text-sm
                    hover:bg-gray-700 transition-colors"
                >
                  <Box className="w-4 h-4" />
                  {t('catalogue.preview3d')}
                </button>
              )}
              {selectedVariant?.manufacturerUrl && (
                <a
                  href={selectedVariant.manufacturerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 rounded-md bg-gray-800 text-white text-xs md:text-sm
                    hover:bg-gray-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  {t('catalogue.visitSite')}
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Variant Modal */}
      <AddVariantModal
        isOpen={isAddVariantModalOpen || !!editingVariantId}
        onClose={() => {
          setIsAddVariantModalOpen(false);
          setEditingVariantId(null);
        }}
        onAddVariant={handleAddOrEditVariant}
        productName={selectedProduct.name}
        editData={editingVariant}
      />

      {/* 3D Preview Modal */}
      <IesPreview3DModal
        isOpen={isPreview3DOpen}
        onClose={() => setIsPreview3DOpen(false)}
        iesUrl={selectedVariant?.ldcFileUrl}
        title={selectedProduct.name}
        subtitle={selectedVariant?.name}
        colorTemperature={selectedVariant?.colorTemperature}
      />

      {/* Delete Variant Confirm Modal */}
      <DeleteConfirmModal
        isOpen={!!deletingVariantData}
        onClose={() => setDeletingVariantData(null)}
        onConfirm={handleConfirmDelete}
        title={t('catalogue.deleteVariantTitle')}
        itemName={deletingVariantData?.name || ''}
        itemType="variant"
      />
    </div>
  );
};

export default ProductDetail;
